import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import '../css/Dashboard.css';
import '../css/Shimmer.css';
import { dashboardAPI } from '../services/api';
import { ShimmerBox } from '../components/Shimmer';

// Dashboard Icons
import totalEarningIcon from '../assets/images/DashboardImages/total_earning.svg';
import completedBookingIcon from '../assets/images/DashboardImages/completed_booking.svg';
import activeBookingIcon from '../assets/images/DashboardImages/active_booking.svg';
import customerRatingIcon from '../assets/images/DashboardImages/customer_rating.svg';

// Empty State Icons
import salesEmptyIcon from '../assets/images/EmptyIcons/Sales-Overview.svg';
import servicesEmptyIcon from '../assets/images/EmptyIcons/Services-Empty.svg';
import customersEmptyIcon from '../assets/images/EmptyIcons/empty-customers.svg';
import bookingsEmptyIcon from '../assets/images/EmptyIcons/Bookings-Not-Found.svg';
import ratingsEmptyIcon from '../assets/images/EmptyIcons/Ratings-eampty.svg';
import xMarkIcon from '../assets/x-mark.svg';

// WebSocket URL - Staging server
// const WS_URL = 'wss://staging.xtrafyl.com/ws/provider/';
// const API_BASE_URL = 'https://staging.xtrafyl.com';

// WebSocket URL - production server
const WS_URL = 'wss://xtrafyl.com/ws/provider/';
const API_BASE_URL = 'https://xtrafyl.com';
const BOOKING_QUEUE_STORAGE_KEY = 'xtrafyl_booking_queue';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState('Month');

  // API Data States
  const [summaryData, setSummaryData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [servicesData, setServicesData] = useState([]);
  const [customersData, setCustomersData] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);

  // Loading States
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [driversLoading, setDriversLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  // New Booking Popup States
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [acceptTimer, setAcceptTimer] = useState(300);

  // WebSocket States
  const [wsConnected, setWsConnected] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);

  // All WebSocket refs — stable, never cause re-renders
  const wsRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const reconnectTimerRef = useRef(null);
  const messageHandlerRef = useRef(null);

  // Booking Queue States
  const [bookingQueue, setBookingQueue] = useState([]);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const queueTimersRef = useRef({});

  // Beep Sound Refs
  const beepIntervalRef = useRef(null);
  const beepTimeoutRef = useRef(null);

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState(null);

  const getAuthToken = () => localStorage.getItem('token');

  // ==================== API FETCH FUNCTIONS ====================

  const fetchSummary = async (period = 'month') => {
    try {
      setSummaryLoading(true);
      const response = await dashboardAPI.getSummary(period.toLowerCase());
      if (response.data.Status === 'Success') setSummaryData(response.data.Summary);
    } catch (error) {
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchSalesOverview = async (period = 'month') => {
    try {
      setSalesLoading(true);
      const response = await dashboardAPI.getSalesOverview(period.toLowerCase());
      if (response.data.Status === 'Success') setSalesData(response.data);
    } catch (error) {
    } finally {
      setSalesLoading(false);
    }
  };

  const fetchMostUsedServices = async () => {
    try {
      setServicesLoading(true);
      const response = await dashboardAPI.getMostUsedServices(5);
      if (response.data.Status === 'Success') setServicesData(response.data.Services || []);
    } catch (error) {
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchTopCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await dashboardAPI.getTopCustomers(5, 'orders');
      if (response.data.Status === 'Success') setCustomersData(response.data.Customers || []);
    } catch (error) {
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchRecentBookings = async () => {
    try {
      setBookingsLoading(true);
      const response = await dashboardAPI.getRecentBookings(1, 5);
      if (response.data.Status === 'Success') setRecentBookings(response.data.Bookings || []);
    } catch (error) {
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchDriverPerformance = async () => {
    try {
      setDriversLoading(true);
      const response = await dashboardAPI.getDriverPerformance(5, 'deliveries');
      if (response.data.Status === 'Success') setDriversData(response.data.Drivers || []);
      else setDriversData([]);
    } catch (error) {
      setDriversData([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const fetchLatestReviews = async () => {
    try {
      setReviewsLoading(true);
      const response = await dashboardAPI.getLatestReviews(1, 5);
      if (response.data.Status === 'Success') setReviewsData(response.data.Reviews || []);
      else setReviewsData([]);
    } catch (error) {
      setReviewsData([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(selectedPeriod);
    fetchSalesOverview(selectedPeriod);
    fetchMostUsedServices();
    fetchTopCustomers();
    fetchRecentBookings();
    fetchDriverPerformance();
    fetchLatestReviews();
  }, []);

  useEffect(() => {
    fetchSalesOverview(selectedPeriod);
  }, [selectedPeriod]);

  // ==================== QUEUE STORAGE ====================

  const saveQueueToStorage = useCallback((queue) => {
    try {
      localStorage.setItem(BOOKING_QUEUE_STORAGE_KEY, JSON.stringify(
        queue.map(b => ({ ...b, saved_at: Date.now() }))
      ));
    } catch (e) {}
  }, []);

  const loadQueueFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(BOOKING_QUEUE_STORAGE_KEY);
      if (!saved) return [];
      const queue = JSON.parse(saved);
      const now = Date.now();
      return queue
        .map(b => {
          if (b.has_expiry === false) return b; // V2: no expiry, keep as-is
          const elapsed = Math.floor((now - (b.saved_at || now)) / 1000);
          const remaining = Math.max(0, (b.time_remaining_seconds || 300) - elapsed);
          return { ...b, time_remaining_seconds: remaining };
        })
        .filter(b => b.has_expiry === false || b.time_remaining_seconds > 0 || b.time_remaining_seconds === 0);
    } catch (e) {
      return [];
    }
  }, []);

  const clearQueueFromStorage = useCallback(() => {
    localStorage.removeItem(BOOKING_QUEUE_STORAGE_KEY);
  }, []);

  // ==================== TOAST ====================

  const showToast = useCallback((type, message, duration = 5000) => {
    setToastMessage({ type, message });
    setTimeout(() => setToastMessage(null), duration);
  }, []);

  // ==================== BOOKING QUEUE ====================

  // V2 (has_expiry=false) → always show. V1 → only if time > 0
  const shouldShowBooking = (booking) => {
    if (booking.has_expiry === false) return true;
    return booking.time_remaining_seconds > 0 && !booking.is_expired;
  };

  const startBookingTimer = useCallback((booking) => {
    const assignmentId = booking.assignment_id;
    if (queueTimersRef.current[assignmentId]) {
      clearInterval(queueTimersRef.current[assignmentId]);
    }
    // V2: no expiry — no countdown needed
    if (booking.has_expiry === false) return;

    let timeRemaining = booking.time_remaining_seconds || 300;
    queueTimersRef.current[assignmentId] = setInterval(() => {
      timeRemaining -= 1;
      setAcceptTimer(Math.max(0, timeRemaining));
      setBookingQueue(prev =>
        prev.map(b =>
          b.assignment_id === assignmentId
            ? { ...b, time_remaining_seconds: Math.max(0, timeRemaining) }
            : b
        )
      );
      if (timeRemaining <= 0) clearInterval(queueTimersRef.current[assignmentId]);
    }, 1000);
  }, []);

  const addToQueue = useCallback((booking) => {
    setBookingQueue(prevQueue => {
      const exists = prevQueue.find(b => b.assignment_id === booking.assignment_id);
      if (exists) return prevQueue;

      const newQueue = [...prevQueue, booking];
      saveQueueToStorage(newQueue);
      startBookingTimer(booking);

      if (newQueue.length === 1) {
        setCurrentQueueIndex(0);
        setCurrentBooking(booking);
        // V2: 99999 so button shows "ACCEPT ORDER" not "ACCEPT ORDER (0:00)"
        setAcceptTimer(booking.has_expiry === false ? 99999 : (booking.time_remaining_seconds || 300));
        setShowNewBooking(true);
      }
      return newQueue;
    });
  }, [saveQueueToStorage, startBookingTimer]);

  const removeFromQueue = useCallback((assignmentId) => {
    if (queueTimersRef.current[assignmentId]) {
      clearInterval(queueTimersRef.current[assignmentId]);
      delete queueTimersRef.current[assignmentId];
    }
    setBookingQueue(prevQueue => {
      const index = prevQueue.findIndex(b => b.assignment_id === assignmentId);
      if (index === -1) return prevQueue;

      const newQueue = prevQueue.filter(b => b.assignment_id !== assignmentId);

      if (newQueue.length === 0) {
        clearQueueFromStorage();
        setShowNewBooking(false);
        setCurrentBooking(null);
        setCurrentQueueIndex(0);
      } else {
        saveQueueToStorage(newQueue);
        const newIndex = Math.min(index, newQueue.length - 1);
        setCurrentQueueIndex(newIndex);
        const nextBooking = newQueue[newIndex];
        setCurrentBooking(nextBooking);
        setAcceptTimer(nextBooking.has_expiry === false ? 99999 : (nextBooking.time_remaining_seconds || 300));
      }
      return newQueue;
    });
  }, [saveQueueToStorage, clearQueueFromStorage]);

  // ==================== WEBSOCKET MESSAGE HANDLER ====================

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connection_established':
        break;

      case 'authenticated':
        if (data.pending_assignments && data.pending_assignments.length > 0) {
          data.pending_assignments.forEach(booking => {
            if (shouldShowBooking(booking)) addToQueue(booking);
          });
        }
        break;

      case 'new_booking_assignment':
        // V2 FIX: has_expiry=false → time_remaining_seconds=null → null > 0 = false (broken)
        // shouldShowBooking() checks has_expiry first so V2 always shows
        if (shouldShowBooking(data)) addToQueue(data);
        break;

      case 'pending_assignments_list':
        if (data.pending_assignments) {
          data.pending_assignments.forEach(booking => {
            if (shouldShowBooking(booking)) addToQueue(booking);
          });
        }
        break;

      case 'booking_assignment_expired':
        removeFromQueue(data.assignment_id);
        break;

      case 'booking_cancelled':
        if (data.action === 'CLOSE_POPUP') {
          removeFromQueue(data.assignment_id);
          if (data.is_reschedule) {
            showToast('info', `Booking ${data.booking_number || ''} was rescheduled by customer. New request arriving shortly.`, 6000);
          } else {
            showToast('warning', `Booking ${data.booking_number || ''} was cancelled by customer.`, 5000);
          }
        }
        break;

      case 'assignment_accepted':
        removeFromQueue(data.assignment_id);
        break;

      case 'assignment_rejected':
        removeFromQueue(data.assignment_id);
        break;

      case 'auth_error':
        break;

      case 'pong':
        break;

      default:
        break;
    }
  }, [addToQueue, removeFromQueue, showToast]);

  // Always keep ref pointing to latest handler version
  useEffect(() => {
    messageHandlerRef.current = handleWebSocketMessage;
  }, [handleWebSocketMessage]);

  // ==================== WEBSOCKET CONNECTION ====================
  // Single useEffect with empty deps [] — runs exactly once on mount.
  // Uses only refs inside so no stale closures, no double-connect from StrictMode.

  useEffect(() => {
    shouldReconnectRef.current = true;

    function connect() {
      const token = getAuthToken();
      if (!token) return;

      // Already open or mid-connect — skip
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) { ws.close(); return; }
        setWsConnected(true);
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (messageHandlerRef.current) messageHandlerRef.current(data);
        } catch (e) {}
      };

      ws.onerror = () => {
        // onclose fires next, handles reconnect
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return; // ghost socket, ignore
        wsRef.current = null;
        setWsConnected(false);
        if (shouldReconnectRef.current && getAuthToken()) {
          reconnectTimerRef.current = setTimeout(connect, 5000);
        }
      };
    }

    connect();

    // Ping every 30s to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      // Intentional unmount — stop reconnecting
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect loop on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }
      clearInterval(pingInterval);
      // Clear all queue timers
      Object.values(queueTimersRef.current).forEach(t => clearInterval(t));
      queueTimersRef.current = {};
    };
  }, []); // ← EMPTY DEPS: single mount/unmount, no re-runs

  // ==================== LOAD SAVED QUEUE ON NAVIGATION ====================

  useEffect(() => {
    let savedQueue = loadQueueFromStorage();
    savedQueue = savedQueue.filter(b => !b.booking_number?.startsWith('TEST-'));

    if (savedQueue.length > 0) {
      setBookingQueue(savedQueue);
      setCurrentQueueIndex(0);
      setCurrentBooking(savedQueue[0]);
      setAcceptTimer(savedQueue[0].has_expiry === false ? 99999 : (savedQueue[0].time_remaining_seconds || 300));
      setShowNewBooking(true);
      savedQueue.forEach(booking => startBookingTimer(booking));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ==================== BEEP SOUND ====================

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/provider/beep.mp3');
      audio.play().catch(() => {});
    } catch (e) {}
  };

  const stopContinuousBeep = () => {
    if (beepIntervalRef.current) { clearInterval(beepIntervalRef.current); beepIntervalRef.current = null; }
    if (beepTimeoutRef.current) { clearTimeout(beepTimeoutRef.current); beepTimeoutRef.current = null; }
  };

  const startContinuousBeep = () => {
    stopContinuousBeep();
    playNotificationSound();
    beepIntervalRef.current = setInterval(() => playNotificationSound(), 10000);
    beepTimeoutRef.current = setTimeout(() => stopContinuousBeep(), 60000);
  };

  useEffect(() => {
    if (showNewBooking) {
      document.body.style.overflow = 'hidden';
      startContinuousBeep();
    } else {
      document.body.style.overflow = 'unset';
      stopContinuousBeep();
    }
    return () => stopContinuousBeep();
  }, [showNewBooking]);

  // ==================== ACCEPT / REJECT ====================

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAcceptOrder = async () => {
    if (!currentBooking) { setShowNewBooking(false); return; }
    const token = getAuthToken();
    if (!token) return;

    const assignmentId = currentBooking.assignment_id;
    const bookingId = currentBooking.booking_id;

    try {
      const response = await fetch(`${API_BASE_URL}/Provider/api/v1/AcceptOrRejectOrder/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ BookingId: bookingId, AcceptStatus: true })
      });
      const data = await response.json();
      if (response.ok) {
        showToast('success', 'Booking accepted successfully!', 5000);
      } else {
        showToast('error', 'Failed to accept booking: ' + (data.Message || data.Error || 'Unknown error'), 5000);
      }
    } catch (error) {
      showToast('error', 'Error accepting booking. Please try again.', 5000);
    }
    removeFromQueue(assignmentId);
  };

  const handleRejectOrder = useCallback(async () => {
    if (!currentBooking) { setShowNewBooking(false); return; }
    const token = getAuthToken();
    if (!token) { setShowNewBooking(false); return; }

    const assignmentId = currentBooking.assignment_id;
    const bookingId = currentBooking.booking_id;

    try {
      const response = await fetch(`${API_BASE_URL}/Provider/api/v1/AcceptOrRejectOrder/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ BookingId: bookingId, AcceptStatus: false })
      });
      const data = await response.json();
      if (response.ok) showToast('info', 'Booking declined', 5000);
    } catch (error) {}

    removeFromQueue(assignmentId);
  }, [currentBooking, removeFromQueue, showToast]);

  // ==================== DATA FORMATTING ====================

  const formatCurrency = (value) =>
    `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

  const getStatsCards = () => {
    if (!summaryData) {
      return [
        { id: 1, title: 'Total Earnings', value: '$0', change: null, changeText: '', borderColor: '#6366F1', icon: totalEarningIcon },
        { id: 2, title: 'Completed Bookings', value: '0', change: null, changeText: '', borderColor: '#EC4899', icon: completedBookingIcon },
        { id: 3, title: 'Active Bookings', value: '0', change: null, changeText: '', borderColor: '#F59E0B', icon: activeBookingIcon },
        { id: 4, title: 'Customer Rating', value: '0', borderColor: '#10B981', icon: customerRatingIcon }
      ];
    }
    return [
      { id: 1, title: 'Total Earnings', value: formatCurrency(summaryData.TotalEarnings?.Value), change: summaryData.TotalEarnings?.Change ? `${summaryData.TotalEarnings.Change}%` : null, changeText: summaryData.TotalEarnings?.Label || 'this month', borderColor: '#6366F1', icon: totalEarningIcon },
      { id: 2, title: 'Completed Bookings', value: formatNumber(summaryData.CompletedBookings?.Value), change: summaryData.CompletedBookings?.Change ? `${summaryData.CompletedBookings.Change}%` : null, changeText: summaryData.CompletedBookings?.Label || 'this month', borderColor: '#EC4899', icon: completedBookingIcon },
      { id: 3, title: 'Active Bookings', value: formatNumber(summaryData.ActiveBookings?.Value), change: summaryData.ActiveBookings?.Change ? `${summaryData.ActiveBookings.Change}%` : null, changeText: summaryData.ActiveBookings?.Label || 'this month', borderColor: '#F59E0B', icon: activeBookingIcon },
      { id: 4, title: 'Customer Rating', value: summaryData.CustomerRating?.Value?.toFixed(1) || '0', change: null, changeText: '', borderColor: '#10B981', icon: customerRatingIcon }
    ];
  };

  const getChartData = () => {
    if (!salesData?.Chart) return [];
    const labels = salesData.Chart.Labels || [];
    const orders = salesData.Chart.Datasets?.Orders || [];
    const sales = salesData.Chart.Datasets?.Sales || [];
    const revenue = salesData.Chart.Datasets?.Revenue || [];
    return labels.map((label, index) => ({
      month: label, Orders: orders[index] || 0, Sales: sales[index] || 0, Revenue: revenue[index] || 0
    }));
  };

  const getSalesSummary = () => {
    if (!salesData?.Summary) return { totalOrders: 0, totalSales: 0, revenueEarned: 0 };
    return {
      totalOrders: salesData.Summary.TotalOrders || 0,
      totalSales: salesData.Summary.TotalSales || 0,
      revenueEarned: salesData.Summary.RevenueEarned || 0
    };
  };

  const statsCards = getStatsCards();
  const chartData = getChartData();
  const salesSummary = getSalesSummary();

  const getBookingDisplayData = () => {
    if (currentBooking) {
      const scheduledDate = currentBooking.scheduled_display
        || (currentBooking.scheduled_at
          ? new Date(currentBooking.scheduled_at).toLocaleString('en-GB', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: 'numeric', minute: '2-digit', hour12: true
            }).replace(',', ',')
          : 'N/A');

      // quantity from backend — show as "X Litres" if present
      const quantity = currentBooking.quantity
        ? `${currentBooking.quantity} Litres`
        : null;

      return {
        scheduledDateTime: scheduledDate,
        serviceLocation:   currentBooking.service_location || currentBooking.location?.address || 'N/A',
        serviceName:       currentBooking.service_name     || 'N/A',
        subServiceName:    currentBooking.sub_service_name || 'N/A',
        baseName:          currentBooking.base_name        || null,
        quantity:          quantity,
        paymentMode:       currentBooking.payment_mode     || 'Card',
      };
    }
    // No real booking yet — show blanks, not fake demo data
    return {
      scheduledDateTime: '—',
      serviceLocation:   '—',
      serviceName:       '—',
      subServiceName:    '—',
      baseName:          null,
      quantity:          null,
      paymentMode:       'Card',
    };
  };

  const bookingDisplayData = getBookingDisplayData();

  const renderStatCardShimmer = () => (
    <div className="dashboard-stat-card" style={{ borderLeft: '4px solid #e2e1f6' }}>
      <div className="dashboard-stat-content">
        <ShimmerBox width="100px" height="14px" />
        <ShimmerBox width="80px" height="28px" style={{ marginTop: '8px' }} />
        <ShimmerBox width="120px" height="12px" style={{ marginTop: '8px' }} />
      </div>
      <ShimmerBox width="32px" height="32px" borderRadius="6px" />
    </div>
  );

  const renderTableRowShimmer = (cols) => (
    <tr>{Array(cols).fill(0).map((_, i) => (<td key={i}><ShimmerBox width="80px" height="14px" /></td>))}</tr>
  );

  const renderEmptyState = (icon, title, message) => (
    <tr>
      <td colSpan="10" className="empty-table-cell">
        <div className="dashboard-empty-state">
          <img src={icon} alt={title} className="empty-state-icon" />
          <h3 className="empty-state-title">{title}</h3>
          <p className="empty-state-text">{message}</p>
        </div>
      </td>
    </tr>
  );

  const getToastIcon = (type) => {
    switch (type) {
      case 'success': return (<svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="40" fill="#22C55E" fillOpacity="0.15"/><circle cx="40" cy="40" r="30" fill="#22C55E"/><path d="M26 40L35 49L54 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>);
      case 'error': return (<svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="40" fill="#EF4444" fillOpacity="0.15"/><circle cx="40" cy="40" r="30" fill="#EF4444"/><path d="M28 28L52 52M52 28L28 52" stroke="white" strokeWidth="4" strokeLinecap="round"/></svg>);
      case 'warning': return (<svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="40" fill="#F59E0B" fillOpacity="0.15"/><circle cx="40" cy="40" r="30" fill="#F59E0B"/><path d="M40 28V44M40 52H40.01" stroke="white" strokeWidth="4" strokeLinecap="round"/></svg>);
      case 'info': default: return (<svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="40" fill="#6366F1" fillOpacity="0.15"/><circle cx="40" cy="40" r="30" fill="#6366F1"/><path d="M40 52V40M40 28H40.01" stroke="white" strokeWidth="4" strokeLinecap="round"/></svg>);
    }
  };

  const getToastStyle = (type) => {
    switch (type) {
      case 'success': return { backgroundColor: '#22C55E', color: 'white' };
      case 'error': return { backgroundColor: '#EF4444', color: 'white' };
      case 'warning': return { backgroundColor: '#F59E0B', color: 'white' };
      case 'info': default: return { backgroundColor: '#5B9BD5', color: 'white' };
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="dashboard-page">

      {/* Toast Notification Popup */}
      {toastMessage && (
        <div
          className="toast-overlay"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}
          onClick={() => setToastMessage(null)}
        >
          <div
            className="toast-popup"
            style={{ background: 'white', borderRadius: '24px', padding: '32px 40px', minWidth: '360px', maxWidth: '90%', textAlign: 'center', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', animation: 'popIn 0.3s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setToastMessage(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src={xMarkIcon} alt="Close" style={{ width: '24px', height: '24px' }} />
            </button>
            <div style={{ marginBottom: '20px' }}>{getToastIcon(toastMessage.type)}</div>
            <p style={{ fontSize: '20px', fontWeight: 600, color: '#1E293B', margin: 0, fontFamily: '"Space Grotesk", sans-serif' }}>
              {toastMessage.message}
            </p>
          </div>
        </div>
      )}

      <Container fluid className="px-4 py-4">
        <Row>
          <Col lg={4}>
            <div className="dashboard-stats-container">
              {summaryLoading ? (
                <>{renderStatCardShimmer()}{renderStatCardShimmer()}{renderStatCardShimmer()}{renderStatCardShimmer()}</>
              ) : (
                statsCards.map((card) => (
                  <div key={card.id} className="dashboard-stat-card" style={{ borderLeft: `4px solid ${card.borderColor}` }}>
                    <div className="dashboard-stat-content">
                      <p className="dashboard-stat-title">{card.title}</p>
                      <h2 className="dashboard-stat-value">{card.value}</h2>
                      {card.change && (
                        <p className="dashboard-stat-change">
                          <span className="dashboard-stat-percent">{card.change}</span> {card.changeText}
                        </p>
                      )}
                    </div>
                    <div className="dashboard-stat-icon"><img src={card.icon} alt={card.title} /></div>
                  </div>
                ))
              )}
            </div>
          </Col>

          <Col lg={8}>
            <div className="dashboard-sales-card">
              <div className="dashboard-sales-header">
                <h3 className="dashboard-sales-title">Sales Overview</h3>
                <div className="dashboard-sales-header-right">
                  <div className="dashboard-chart-legend">
                    <div className="dashboard-legend-item"><span className="dashboard-legend-dot orders"></span><span>Orders</span></div>
                    <div className="dashboard-legend-item"><span className="dashboard-legend-dot sales"></span><span>Sales</span></div>
                    <div className="dashboard-legend-item"><span className="dashboard-legend-dot revenue"></span><span>Revenue</span></div>
                  </div>
                  <div className="dashboard-period-selector">
                    {['Day', 'Week', 'Month', 'Year'].map((period) => (
                      <button key={period} className={`dashboard-period-btn ${selectedPeriod === period ? 'active' : ''}`} onClick={() => setSelectedPeriod(period)}>
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dashboard-sales-chart">
                {salesLoading ? (
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: '300px', padding: '20px' }}>
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                        <ShimmerBox width="10px" height={`${50 + Math.random() * 150}px`} borderRadius="3px 3px 0 0" />
                        <ShimmerBox width="10px" height={`${50 + Math.random() * 150}px`} borderRadius="3px 3px 0 0" />
                        <ShimmerBox width="10px" height={`${50 + Math.random() * 150}px`} borderRadius="3px 3px 0 0" />
                      </div>
                    ))}
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} barSize={10}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="month" tick={{ fill: '#4F565E', fontSize: 14, fontFamily: 'DM Sans' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#4F565E', fontSize: 14, fontFamily: 'DM Sans' }} axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Bar dataKey="Orders" fill="#8362E5" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Sales" fill="#FFB3E9" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Revenue" fill="#D1B5FF" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="dashboard-empty-state" style={{ height: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={salesEmptyIcon} alt="No Sales Data" className="empty-state-icon" />
                    <h3 className="empty-state-title">No Sales Data</h3>
                    <p className="empty-state-text">No sales data available for this period.</p>
                  </div>
                )}
              </div>

              <div className="dashboard-sales-stats">
                <div className="dashboard-sales-stat-item">
                  <div className="dashboard-sales-stat-label">Total Orders</div>
                  <div className="dashboard-sales-stat-value orders">{salesLoading ? <ShimmerBox width="60px" height="20px" /> : formatNumber(salesSummary.totalOrders)}</div>
                </div>
                <div className="dashboard-sales-stat-item">
                  <div className="dashboard-sales-stat-label">Total Sales</div>
                  <div className="dashboard-sales-stat-value sales">{salesLoading ? <ShimmerBox width="60px" height="20px" /> : formatNumber(salesSummary.totalSales)}</div>
                </div>
                <div className="dashboard-sales-stat-item">
                  <div className="dashboard-sales-stat-label">Revenue Earned</div>
                  <div className="dashboard-sales-stat-value revenue">{salesLoading ? <ShimmerBox width="60px" height="20px" /> : formatCurrency(salesSummary.revenueEarned)}</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Most Used Services & Top Customers */}
        <Row className="mt-4">
          <Col lg={6}>
            <div className="dashboard-table-card">
              <div className="dashboard-table-header">
                <h3 className="dashboard-table-title">Most Used Services</h3>
                <span className="dashboard-view-all" onClick={() => navigate('/services')}>View All →</span>
              </div>
              <div className="table-scroll-wrapper">
              <table className="dashboard-table">
                <thead><tr><th>Service</th><th>Orders</th><th>Revenue</th></tr></thead>
                <tbody>
                  {servicesLoading ? (
                    <>{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}</>
                  ) : servicesData.length > 0 ? (
                    servicesData.map((service, index) => (
                      <tr key={service.ServiceId || index}>
                        <td>{service.Service}</td><td>{formatNumber(service.Orders)}</td>
                        <td>{service.RevenueDisplay || formatCurrency(service.Revenue)}</td>
                      </tr>
                    ))
                  ) : renderEmptyState(servicesEmptyIcon, 'No Services Found', 'No services data available at the moment.')}
                </tbody>
              </table>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="dashboard-table-card">
              <div className="dashboard-table-header">
                <h3 className="dashboard-table-title">Top Customers</h3>
              </div>
              <div className="table-scroll-wrapper">
              <table className="dashboard-table">
                <thead><tr><th>Name</th><th>Orders</th><th>Earnings</th></tr></thead>
                <tbody>
                  {customersLoading ? (
                    <>{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}</>
                  ) : customersData.length > 0 ? (
                    customersData.map((customer, index) => (
                      <tr key={customer.CustomerId || index}>
                        <td>{customer.Name}</td><td>{formatNumber(customer.Orders)}</td>
                        <td>{customer.EarningsDisplay || formatCurrency(customer.Earnings)}</td>
                      </tr>
                    ))
                  ) : renderEmptyState(customersEmptyIcon, 'No Customers Found', 'No customers data available at the moment.')}
                </tbody>
              </table>
              </div>
            </div>
          </Col>
        </Row>

        {/* Recent Bookings */}
        <div className="dashboard-table-card mt-4">
          <div className="dashboard-table-header">
            <h3 className="dashboard-table-title">Recent Bookings</h3>
            <span className="dashboard-view-all" onClick={() => navigate('/bookings')}>View All →</span>
          </div>
          <div className="table-scroll-wrapper">
          <table className="dashboard-table">
            <thead>
              <tr><th>Booking ID</th><th>Date</th><th>Customer</th><th>Service</th><th>Delivery Location</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {bookingsLoading ? (
                <>{renderTableRowShimmer(7)}{renderTableRowShimmer(7)}{renderTableRowShimmer(7)}</>
              ) : recentBookings.length > 0 ? (
                recentBookings.map((booking, index) => (
                  <tr key={booking.BookingId || index}>
                    <td>{booking.BookingIdDisplay || `#${booking.BookingId}`}</td>
                    <td>{booking.Date}<br/>{booking.Time}</td>
                    <td>{booking.Customer}</td><td>{booking.Service}</td>
                    <td>{booking.DeliveryLocation}</td>
                    <td>{booking.AmountDisplay || formatCurrency(booking.Amount)}</td>
                    <td>
                      <span className={`dashboard-status-badge ${booking.StatusGroup || booking.Status?.toLowerCase()}`}>
                        {booking.Status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : renderEmptyState(bookingsEmptyIcon, 'No Bookings Found', 'No recent bookings available at the moment.')}
            </tbody>
          </table>
          </div>
        </div>

        {/* Driver Performance & Latest Reviews */}
        <Row className="mt-4">
          <Col lg={6}>
            <div className="dashboard-table-card">
              <div className="dashboard-table-header">
                <h3 className="dashboard-table-title">Driver Performance</h3>
              </div>
              <div className="table-scroll-wrapper">
              <table className="dashboard-table">
                <thead><tr><th>Driver</th><th>Deliveries</th><th>Rating</th><th>Earnings</th></tr></thead>
                <tbody>
                  {driversLoading ? (
                    <>{renderTableRowShimmer(4)}{renderTableRowShimmer(4)}{renderTableRowShimmer(4)}{renderTableRowShimmer(4)}{renderTableRowShimmer(4)}</>
                  ) : driversData.length > 0 ? (
                    driversData.map((driver, index) => (
                      <tr key={driver.DriverId || index}>
                        <td>{driver.Driver}</td><td>{formatNumber(driver.Deliveries)}</td>
                        <td><span className="rating-cell">{driver.Rating?.toFixed(1) || '0.0'} <span className="star-icon">★</span></span></td>
                        <td>{driver.EarningsDisplay || formatCurrency(driver.Earnings)}</td>
                      </tr>
                    ))
                  ) : renderEmptyState(customersEmptyIcon, 'No Drivers Found', 'No driver performance data available at the moment.')}
                </tbody>
              </table>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="dashboard-table-card">
              <div className="dashboard-table-header">
                <h3 className="dashboard-table-title">Latest Reviews</h3>
                <span className="dashboard-view-all" onClick={() => navigate('/reviews')}>View All →</span>
              </div>
              <div className="table-scroll-wrapper">
              <table className="dashboard-table reviews-table">
                <thead><tr><th>Name & Service</th><th>Ratings</th><th>Comments</th></tr></thead>
                <tbody>
                  {reviewsLoading ? (
                    <>{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}{renderTableRowShimmer(3)}</>
                  ) : reviewsData.length > 0 ? (
                    reviewsData.map((review, index) => (
                      <tr key={review.ReviewId || index}>
                        <td>
                          <div className="review-name-service">
                            <span className="review-name">{review.Name}</span>
                            <span className="review-service">{review.Service}</span>
                          </div>
                        </td>
                        <td><span className="rating-cell">{review.Rating?.toFixed(1) || review.DriverRating?.toFixed(1) || '0.0'} <span className="star-icon">★</span></span></td>
                        <td className="review-comment">{review.Comment || '-'}</td>
                      </tr>
                    ))
                  ) : renderEmptyState(ratingsEmptyIcon, 'No Reviews Found', 'No reviews available at the moment.')}
                </tbody>
              </table>
              </div>
            </div>
          </Col>
        </Row>

      </Container>

      {/* New Booking Popup */}
      {showNewBooking && (
        <div className="booking-popup-overlay">
          <div className="booking-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="booking-popup-header">
              <h3>
                New Booking
                {bookingQueue.length > 1 && <span className="queue-indicator">({currentQueueIndex + 1}/{bookingQueue.length})</span>}
              </h3>
            </div>

            <div className="booking-details dashboard_pop">
              {/* Scheduled Date & Time */}
              <div className="booking-row">
                <span className="booking-label">Scheduled Date & Time</span>
                <span className="booking-value">{bookingDisplayData.scheduledDateTime}</span>
              </div>

              {/* Service Location — customer address */}
              <div className="booking-row">
                <span className="booking-label">Service Location</span>
                <span className="booking-value">{bookingDisplayData.serviceLocation}</span>
              </div>

              {/* Service — main service name */}
              <div className="booking-row">
                <span className="booking-label">Service</span>
                <span className="booking-value">{bookingDisplayData.serviceName}</span>
              </div>

              {/* Service Detail — sub service name */}
              <div className="booking-row">
                <span className="booking-label">Service Detail</span>
                <span className="booking-value">{bookingDisplayData.subServiceName}</span>
              </div>

              {/* Nearest Base Location — only if base_name exists */}
              {bookingDisplayData.baseName && (
                <div className="booking-row">
                  <span className="booking-label">Nearest Base Location</span>
                  <span className="booking-value">{bookingDisplayData.baseName}</span>
                </div>
              )}

              {/* Quantity — only if present (fuel/tire services) */}
              {bookingDisplayData.quantity && (
                <div className="booking-row">
                  <span className="booking-label">Quantity</span>
                  <span className="booking-value">{bookingDisplayData.quantity}</span>
                </div>
              )}

              {/* Payment Mode */}
              <div className="booking-row">
                <span className="booking-label">Payment Mode</span>
                <span className="booking-value">{bookingDisplayData.paymentMode}</span>
              </div>
            </div>

            <div className="booking-actions">
              <button className="booking-btn booking-btn-reject" onClick={handleRejectOrder}>
                REJECT
              </button>
              <button className="booking-btn booking-btn-accept" onClick={handleAcceptOrder}>
                {/* V2: no-expiry → hide countdown. V1: show MM:SS */}
                ACCEPT ORDER {currentBooking?.has_expiry === false ? '' : `(${formatTimer(acceptTimer)})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes popIn {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default Dashboard;